'use client';

import { Box, Card, CardContent, Typography, Chip, Button, Stack } from '@mui/material';
import { Calendar, DollarSign, Clock, CheckCircle, XCircle } from 'lucide-react';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';

interface Invitation {
    id: string;
    title: string;
    company: string;
    status: 'pending' | 'accepted' | 'rejected';
    budget: string;
    deadline: string;
    description: string;
    receivedDate: string;
    companyLogo?: string;
}

interface InvitationsListProps {
    invitations: Invitation[];
    onAccept: (invitationId: string) => void;
    onReject: (invitationId: string) => void;
}

export function InvitationsList({ invitations, onAccept, onReject }: InvitationsListProps) {
    const { themeStyle } = useThemeContext();
    const cardBg = theme[themeStyle].secondaryBg;
    const textColor = theme[themeStyle].tag_white;

    const getStatusChip = (status: string) => {
        switch (status) {
            case 'accepted':
                return {
                    label: 'Accepted',
                    sx: { backgroundColor: '#dcfce7', color: '#15803d' },
                };
            case 'rejected':
                return {
                    label: 'Rejected',
                    sx: { backgroundColor: '#fee2e2', color: '#b91c1c' },
                };
            case 'pending':
                return {
                    label: 'New Invitation',
                    sx: { backgroundColor: '#dbeafe', color: '#1e40af' },
                };
            default:
                return {
                    label: 'Unknown',
                    sx: { backgroundColor: '#f3f4f6', color: '#374151' },
                };
        }
    };

    return (
        <Stack spacing={2}>
            {invitations.length === 0 ? (
                <Card sx={{ backgroundColor: cardBg }}>
                    <CardContent sx={{ p: 5, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: textColor }}>
                            No invitations found
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                invitations.map(invitation => (
                    <Card
                        key={invitation.id}
                        sx={{
                            backgroundColor: cardBg,
                            borderColor: invitation.status === 'pending' ? '#bfdbfe' : undefined,
                            borderWidth: invitation.status === 'pending' ? 1 : undefined,
                        }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                <Box flex={1}>
                                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                                        <Typography variant="h6" fontWeight={600} sx={{ color: textColor }}>
                                            {invitation.title}
                                        </Typography>
                                        <Chip size="small" {...getStatusChip(invitation.status)} />
                                    </Box>

                                    <Typography variant="body2" sx={{ color: textColor }} mb={0.5}>
                                        {invitation.company}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: textColor }} mb={2}>
                                        {invitation.description}
                                    </Typography>

                                    <Stack direction="row" spacing={3} fontSize="0.875rem" sx={{ color: textColor }}>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <DollarSign size={16} style={{ color: textColor }} />
                                            <span>{invitation.budget}</span>
                                        </Box>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <Calendar size={16} style={{ color: textColor }} />
                                            <span>{invitation.deadline}</span>
                                        </Box>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <Clock size={16} style={{ color: textColor }} />
                                            <span>Received {invitation.receivedDate}</span>
                                        </Box>
                                    </Stack>
                                </Box>

                                <Box ml={3} display="flex" flexDirection="column" gap={1}>
                                    {invitation.status === 'pending' ? (
                                        <Stack direction="row" spacing={1}>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<CheckCircle size={16} />}
                                                sx={{
                                                    backgroundColor: '#16a34a',
                                                    '&:hover': { backgroundColor: '#15803d' },
                                                    color: '#fff',
                                                }}
                                                onClick={() => onAccept(invitation.id)}>
                                                Accept
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<XCircle size={16} />}
                                                sx={{
                                                    color: '#dc2626',
                                                    borderColor: '#fecaca',
                                                    '&:hover': {
                                                        backgroundColor: '#fef2f2',
                                                    },
                                                }}
                                                onClick={() => onReject(invitation.id)}>
                                                Reject
                                            </Button>
                                        </Stack>
                                    ) : (
                                        <Typography variant="body2" sx={{ color: textColor }}>
                                            {invitation.status === 'accepted' ? '✓ Accepted' : '✗ Rejected'}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                ))
            )}
        </Stack>
    );
}
